import { contactSales, contactSupport } from "@src/utils/contactSupport";
import { bookSmartPricingConsultation } from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

import {
  SMART_CHECKOUT_ID,
  SMART_CONTENT_ID,
  SMART_MERCHANDISING_ID,
  SMART_PRICING_LIMITED_ID,
  SMART_PRICING_UNLIMITED_ID,
  SMART_PRICING_VARIANTS,
  SMART_SHIPPING_ID,
  UNLIMITED_ID,
} from "@src/constants/billing";
import type {
  BillingModuleGateDecision,
  GatedModuleButton,
} from "./PricingGatedModal.types";
import {
  SMART_PRICING_GATE_COPY,
  SMART_PRICING_GATE_FEATURES,
} from "./gateModuleCopy";
import { mpSmartPricingSalesContactStarted } from "@src/utils/mixpanel";

type PricingGatedModalViewModelProps = {
  decision: BillingModuleGateDecision;
  isOnBillingPage: boolean;
  moduleId: string;
  onClose: () => void;
  onContinueToPayment?: () => void;
};

// Figma labels the self-serve "add" CTA per module (e.g. "Add Smart Checkout"
// uses the short module name, not "Smart Checkout & Post-purchase").
const ADD_MODULE_CTA_LABEL: Readonly<Record<string, string>> = {
  [SMART_CHECKOUT_ID]: "Add Smart Checkout",
  [SMART_CONTENT_ID]: "Add Smart Content",
  [SMART_MERCHANDISING_ID]: "Add Smart Merchandising",
  [SMART_PRICING_LIMITED_ID]: "Add Smart Pricing",
  [SMART_PRICING_UNLIMITED_ID]: "Add Smart Pricing",
  [SMART_SHIPPING_ID]: "Add Smart Shipping",
};

function getGateModuleDetails({
  id,
  includedModuleNames,
  tier,
}: {
  id: string;
  includedModuleNames: readonly string[];
  tier: BillingModuleGateDecision["tier"];
}) {
  return {
    [SMART_CHECKOUT_ID]: {
      description:
        "Test and personalize on recommended products, offers, and trust signals to convert more at checkout.",
      discount: 10,
      features: ["Dynamic upsells", "Post-purchase offers", "Trust badges"],
      name: "Smart Checkout & Post-purchase",
      title: "Make more money at checkout",
    },
    [SMART_MERCHANDISING_ID]: {
      description:
        "Test bundle offers, progress bars, and quantity breaks to drive higher cart values before checkout.",
      discount: 10,
      features: [
        "Discounts & GWP",
        "PDP bundles & cart upsells",
        "Quantity breaks & progress bars",
      ],
      name: "Smart Merchandising",
      title: "Turn browsers into bigger buyers",
    },
    [SMART_PRICING_LIMITED_ID]: {
      description:
        "A/B test and personalize prices, discounts, and offers to lift AOV and protect your margins.",
      discount: 10,
      features: SMART_PRICING_GATE_FEATURES,
      isExcludedFromTrial: SMART_PRICING_GATE_COPY.isExcludedFromTrial,
      name: "Smart Pricing",
      title: SMART_PRICING_GATE_COPY.title,
    },
    [SMART_PRICING_UNLIMITED_ID]: {
      description:
        "A/B test and personalize prices, discounts, and offers to lift AOV and protect your margins.",
      discount: 10,
      features: SMART_PRICING_GATE_FEATURES,
      isExcludedFromTrial: SMART_PRICING_GATE_COPY.isExcludedFromTrial,
      name: "Smart Pricing",
      title: SMART_PRICING_GATE_COPY.title,
    },
    [SMART_SHIPPING_ID]: {
      description:
        "Test and personalize shipping rates, free shipping thresholds, and offers to reduce cart abandonment.",
      discount: 10,
      features: [
        "Shipping rates",
        "Free shipping thresholds",
        "Free shipping offers",
      ],
      name: "Smart Shipping",
      title: "Optimize every shipping decision",
    },
    [UNLIMITED_ID]: {
      description:
        tier === "high"
          ? "Every module plus expert support, custom integrations, and unlimited testing."
          : "Every module plus custom integrations and unlimited testing.",
      modules: includedModuleNames,
      name: "Unlimited",
      title: "Get the full power of Intelligems",
    },
  }[id];
}

function getModuleButtons({
  addLabel,
  decision,
  isOnBillingPage,
  isSmartPricing,
  moduleId,
  onContinueToPayment,
}: {
  addLabel: string;
  decision: BillingModuleGateDecision;
  isOnBillingPage: boolean;
  isSmartPricing: boolean;
  moduleId: string;
  onContinueToPayment: () => void;
}): GatedModuleButton[] {
  const sharedSx = {
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "120%",
  };

  const addModulePrimary: GatedModuleButton = {
    label: addLabel,
    onClick: onContinueToPayment,
  };

  // Non-pricing modules use a single Figma CTA layout for every cohort: an
  // outlined "View demo" (rendered in the content footer) plus the primary
  // "Add {Module}" self-serve button. Only Smart Pricing varies its CTAs by
  // segment and order-volume tier.
  if (!isSmartPricing) {
    return [addModulePrimary];
  }

  const addModuleSecondary: GatedModuleButton = {
    label: addLabel,
    onClick: onContinueToPayment,
    variant: "outlined",
  };

  const contactUsButton: GatedModuleButton = {
    label: "Contact Us",
    onClick: contactSupport,
    sx: sharedSx,
    variant: "outlined",
  };

  if (decision.segment === "existing_customer") {
    if (decision.tier === "low") {
      return [contactUsButton, addModulePrimary];
    }

    return [
      addModuleSecondary,
      {
        label: "Book a Consultation",
        onClick: () => openNewTab(bookSmartPricingConsultation),
      },
    ];
  }

  if (decision.tier === "low") {
    return [contactUsButton, addModulePrimary];
  }

  const speakToSalesButton: GatedModuleButton = {
    label: "Consult pricing specialist",
    onClick: () => {
      if (
        moduleId === SMART_PRICING_LIMITED_ID ||
        moduleId === SMART_PRICING_UNLIMITED_ID
      ) {
        mpSmartPricingSalesContactStarted({
          ctaLabel: "Consult pricing specialist",
          orderVolumeTier: decision.tier,
          userState: decision.userState,
        });
      }
      contactSales();
    },
  };

  if (decision.tier === "mid") {
    return [addModuleSecondary, speakToSalesButton];
  }

  // add high

  return [...(isOnBillingPage ? [addModuleSecondary] : []), speakToSalesButton];
}

export function getPricingGatedModalViewModel({
  decision,
  isOnBillingPage,
  moduleId,
  onClose,
  onContinueToPayment,
}: PricingGatedModalViewModelProps) {
  const gateModuleDetails = getGateModuleDetails({
    id: moduleId,
    includedModuleNames: decision.includedModuleNames ?? [],
    tier: decision.tier,
  });

  if (!gateModuleDetails) {
    return null;
  }

  const isSmartPricing = SMART_PRICING_VARIANTS.has(moduleId);
  const addLabel =
    ADD_MODULE_CTA_LABEL[moduleId] ?? `Add ${gateModuleDetails.name}`;

  const buttons = getModuleButtons({
    addLabel,
    decision,
    isOnBillingPage,
    isSmartPricing,
    moduleId,
    onContinueToPayment: onContinueToPayment ?? onClose,
  });

  // Non-pricing modules always surface "View demo" as a footer button next to
  // the "Add {Module}" CTA. For Smart Pricing the inline demo is the default,
  // except high-volume prospects outside the billing page, who get the
  // standalone footer "View demo" button instead.
  const showInlineViewDemo =
    isSmartPricing &&
    !(
      decision.segment === "prospect" &&
      decision.tier === "high" &&
      !isOnBillingPage
    );

  return {
    buttons,
    gateModuleDetails,
    moduleId,
    orderVolumeTier: decision.tier,
    showInlineViewDemo,
    userState: decision.userState,
    videoPlaybackId: decision.videoPlaybackId,
  };
}
